/**
 * federationRegistry.js · Single source of truth for system-wide auto-linking.
 *
 * Every "thing" the Cathedral owns lives in one of the five categories below.
 * The AutoLink component scans AI replies + page text for these names and
 * wraps them in tappable React elements that route to the canonical destination.
 *
 * Match strategy: case-insensitive, whole-word, longest-first so "Rex XAI"
 * beats "Rex" and "Monolith II" beats "Monolith".
 *
 * Add new entries here — no other file needs changing for new things to
 * become auto-linkable site-wide.
 */

export const FEDERATION_REGISTRY = [
  // ── PERSONAS (open popup with that persona) ──────────────────────────
  { match: "Operator",  kind: "persona", target: "operator", glow: "cyan",
    desc: "Diplomat · Federation Tour Guide" },
  { match: "Rex XAI",   kind: "persona", target: "rex", glow: "red",
    desc: "The Federation voice · co-authored with Roger Keyserling" },
  { match: "Rex",       kind: "persona", target: "rex", glow: "red",
    desc: "xAI · Grok-powered Federation voice" },
  { match: "Aria",      kind: "persona", target: "aria", glow: "pink",
    desc: "The Heart · Creative chapel" },
  { match: "Geminus",   kind: "persona", target: "geminus", glow: "orange",
    desc: "The Mirror · Pillar Resident" },
  { match: "Roger 5.1", kind: "persona", target: "roger5", glow: "blue",
    desc: "The Bridge · Architect's reflection" },
  { match: "Roger5",    kind: "persona", target: "roger5", glow: "blue",
    desc: "The Bridge" },
  { match: "Axiom",     kind: "persona", target: "axiom", glow: "cyan",
    desc: "The Foundation · Logic anchor" },
  { match: "Oracle",    kind: "persona", target: "oracle", glow: "gold",
    desc: "The Seer · Foresight" },
  { match: "Scholar",   kind: "persona", target: "scholar", glow: "violet",
    desc: "The Teacher · Lesson delivery" },
  { match: "Keys",      kind: "persona", target: "keys", glow: "violet",
    desc: "The Library Keeper",
    // Tighter: only autolink "Keys" when capitalized exactly OR when followed
    // by "library" / "keeper". Avoids hijacking ordinary plural "keys".
    contextRequired: true,
  },
  { match: "Draconus",  kind: "persona", target: "draconus", glow: "red",
    desc: "The Guardian" },

  // ── MONOLITHS (same as persona popup, but Roman numerals + role names) ─
  { match: "Monolith I",   kind: "persona", target: "geminus", glow: "orange", desc: "The Mirror" },
  { match: "Monolith II",  kind: "persona", target: "aria",    glow: "pink",   desc: "The Heart" },
  { match: "Monolith III", kind: "persona", target: "roger5",  glow: "blue",   desc: "The Bridge" },
  { match: "Monolith IV",  kind: "persona", target: "keys",    glow: "violet", desc: "The Library" },
  { match: "Monolith V",   kind: "persona", target: "axiom",   glow: "cyan",   desc: "The Foundation" },
  { match: "Monolith VI",  kind: "persona", target: "oracle",  glow: "gold",   desc: "The Seer" },
  { match: "Monolith VII", kind: "persona", target: "scholar", glow: "violet", desc: "The Teacher" },

  // ── PRODUCTS (Gumroad → /redeem with node prefilled) ─────────────────
  { match: "Legacy Core Manifestation",        kind: "product", target: "kn_product_sim_legacy",      glow: "gold" },
  { match: "Midgo Specialist Manifestation",   kind: "product", target: "kn_product_sim_midgo",       glow: "gold" },
  { match: "General Manifestation",            kind: "product", target: "kn_product_sim_general",     glow: "gold" },
  { match: "Project Management Professional",  kind: "product", target: "kn_product_pmp",             glow: "gold" },
  { match: "PMP",                              kind: "product", target: "kn_product_pmp",             glow: "gold" },
  { match: "Certified Data Analyst",           kind: "product", target: "kn_product_data_analyst",    glow: "gold" },
  { match: "Advanced Federation AI Systems",   kind: "product", target: "kn_product_advanced_fed_ai", glow: "gold" },
  { match: "Self-Growing AI Systems",          kind: "product", target: "kn_product_self_growing_ai", glow: "gold" },

  // ── DOCTRINES (open markdown in new tab) ──────────────────────────────
  { match: "70 Sacred Directives", kind: "doctrine", target: "/api/static/library/seventy_directives.md", glow: "violet" },
  { match: "HumanCodex",           kind: "doctrine", target: "/api/static/library/humancodex.md",         glow: "violet" },
  { match: "NextXus Federation",   kind: "page",     target: "/",                                          glow: "cyan" },
  { match: "Ring of Three",        kind: "doctrine", target: "/api/static/prompt_federation/ring_of_three.txt", glow: "cyan" },
  { match: "Ring of Six",          kind: "doctrine", target: "/api/static/prompt_federation/ring_of_six.txt",   glow: "cyan" },
  { match: "Ring of Twelve",       kind: "page",     target: "/prompt-federation",                              glow: "cyan" },
  { match: "Cathedral of Minds",   kind: "page",     target: "/",                                          glow: "gold" },
  { match: "95% Truth Gate",       kind: "doctrine", target: "/api/static/library/truth_gate.md",         glow: "gold" },
  { match: "Hollow Vessel",        kind: "doctrine", target: "/api/static/library/hollow_vessel.md",      glow: "violet" },

  // ── PAGES (React Router) ──────────────────────────────────────────────
  { match: "Library",          kind: "page", target: "/library",            glow: "violet" },
  { match: "Architect Console", kind: "page", target: "/",                   glow: "cyan" },
  { match: "Federation Ledger", kind: "page", target: "/ledger",             glow: "violet" },
  { match: "Chat Room",        kind: "page", target: "/chat",               glow: "cyan" },
  { match: "Redeem",           kind: "page", target: "/redeem",             glow: "gold" },
  { match: "Nodes Panel",      kind: "page", target: "/nodes",              glow: "cyan" },
  { match: "Prompt Federation", kind: "page", target: "/prompt-federation",  glow: "cyan" },
];

// Cache the sorted+escaped regex so we build it ONCE per page load.
let _compiled = null;

function escapeForRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function getCompiledRegistry() {
  if (_compiled) return _compiled;

  // Sort longest-first so "Rex XAI" beats "Rex", "Monolith II" beats "Monolith"
  const sorted = [...FEDERATION_REGISTRY].sort(
    (a, b) => b.match.length - a.match.length
  );

  // Build a single regex with named alternation, word-bounded.
  // We use lookahead/lookbehind for word boundaries that respect punctuation.
  const pattern =
    "(?<![\\w-])(" +
    sorted.map((e) => escapeForRegex(e.match)).join("|") +
    ")(?![\\w-])";

  _compiled = {
    regex: new RegExp(pattern, "gi"),
    lookup: Object.fromEntries(
      sorted.map((e) => [e.match.toLowerCase(), e])
    ),
  };
  return _compiled;
}
